import { FormInput, FormLabel } from "../../../base-components/Form";
import Button from "../../../base-components/Button";
import clsx from "clsx";
import _ from "lodash";
import logo from "../../../assets/images/brand/logo.webp";
import { connect } from "react-redux";
import { RootState } from "src/stores";
import { useEffect, useState } from "react";
import {
  changePasswordService,
  sendOtpService,
  submitOtpService,
} from "src/services";
import { Eye, EyeOff } from "lucide-react";

const Component = ({}) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [crmVersion, setCrmVersion] = useState<any>("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [redirectTimer, setRedirectTimer] = useState(5);
  const [resendTimer, setResendTimer] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const setingVerion = setTimeout(() => {
      setCrmVersion(process.env.REACT_APP_CRM_VERSION);
    }, 500);
    return () => clearTimeout(setingVerion);
  }, []);

  // Step 1: Send OTP

  const handleOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSendOtpLogic();
  };

  // Shared logic for sending OTP (used for both initial and resend)
  const handleSendOtpLogic = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    const response = await sendOtpService({ email });
    if (response && response.status) {
      setLoading(false);
      setStep(1);
      setResendTimer(30); // Start resend timer
    } else {
      setError("Failed to send OTP. Please try again.");
      setLoading(false);
    }
  };
  // Timer for resend OTP button
  useEffect(() => {
    if (step === 1 && resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer((t) => t - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [step, resendTimer]);

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join("");
    setLoading(true);
    try {
      const response = await submitOtpService({
        email,
        otp: otpValue,
      });

      if (response && response.status) {
        setLoading(false);
        setStep(2);
        setError("");
      } else {
        setLoading(false);
        setError("Invalid OTP. Please try again.");
      }
    } catch (error) {
      setLoading(false);
      setError("Invalid OTP. Please try again.");
    }
  };

  // Handle OTP box change
  const handleOtpChange = (value: string, idx: number) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[idx] = value;
    setOtp(newOtp);
    // Auto-focus next box if value entered
    if (value && idx < 5) {
      const next = document.getElementById(`otp-box-${idx + 1}`);
      if (next) (next as HTMLInputElement).focus();
    }
    // Auto-focus previous box if deleted
    if (!value && idx > 0) {
      const prev = document.getElementById(`otp-box-${idx - 1}`);
      if (prev) (prev as HTMLInputElement).focus();
    }
  };

  // Step 3: Reset Password
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    // Password strength validation
    const passwordRequirements = [
      { label: "At least 6 characters", test: /.{6,}/ },
      { label: "At least 1 uppercase letter", test: /[A-Z]/ },
      { label: "At least 1 number", test: /[0-9]/ },
      { label: "At least 1 special character", test: /[^A-Za-z0-9]/ },
    ];
    const failed = passwordRequirements.filter(
      (req) => !req.test.test(password)
    );
    if (failed.length > 0) {
      setError(`Password must have: ${failed.map((f) => f.label).join(", ")}`);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    setLoading(true);
    const payload = {
      email,
      otp: otp.join(""),
      password,
      password_confirmation: confirmPassword,
    };

    const response = await changePasswordService({
      email,
      otp: otp.join(""),
      password,
      password_confirmation: confirmPassword,
    });

    if (response && response.status) {
      setLoading(false);
      setStep(3);
      setError("");
      setRedirectTimer(5);
    } else {
      setLoading(false);
      setError(
        response?.message || "Failed to reset password. Please try again."
      );
      return;
    }
  };

  // Timer for redirect after success
  useEffect(() => {
    if (step === 3 && redirectTimer > 0) {
      const timer = setTimeout(() => {
        setRedirectTimer((t) => t - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (step === 3 && redirectTimer === 0) {
      window.location.href = "/login";
    }
  }, [step, redirectTimer]);

  return (
    <>
      <div className="container grid grid-cols-12 lg:max-w-[1550px] 2xl:max-w-[1750px] py-10 px-5 sm:py-14 sm:px-10 md:px-36 lg:py-0 lg:pl-14 lg:pr-12 xl:px-24">
        <div
          className={clsx([
            "relative z-50 h-full col-span-12 p-7 sm:p-14 bg-white rounded-2xl lg:bg-transparent lg:pr-10 lg:col-span-5 xl:pr-24 2xl:col-span-4 lg:p-0",
            "before:content-[''] before:absolute before:inset-0 before:-mb-3.5 before:bg-white/40 before:rounded-2xl before:mx-5 ",
          ])}
        >
          <div className="relative z-10 w-full h-full py-2 lg:py-32">
            <img src={logo} className="w-40" />
            <div className="mt-10 flex flex-col justify-center h-full">
              <div className="text-2xl font-medium">Password Reset</div>
              <div className="mt-6">
                {/* Step 1: Email */}
                {step === 0 && (
                  <form onSubmit={handleOtp}>
                    <FormLabel>Email*</FormLabel>
                    <FormInput
                      required
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block px-4 py-3.5 rounded-[0.6rem] border-slate-300/80"
                      placeholder="Enter your email"
                    />
                    {error && (
                      <div className="text-red-500 mt-2 text-center">
                        {error}
                      </div>
                    )}
                    <div className="mt-5 text-center xl:mt-8 xl:text-left">
                      <Button
                        type="submit"
                        variant="primary"
                        rounded
                        className="bg-gradient-to-r from-primary/70 to-secondary/70 w-full py-3.5 xl:mr-3"
                        loading={loading}
                      >
                        Send OTP
                      </Button>
                    </div>
                  </form>
                )}
                {/* Step 2: OTP */}
                {step === 1 && (
                  <form onSubmit={handleVerifyOtp}>
                    <FormLabel>Enter OTP*</FormLabel>
                    <div className="flex gap-3 justify-center mt-4">
                      {otp.map((digit, idx) => (
                        <input
                          key={idx}
                          id={`otp-box-${idx}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(e.target.value, idx)}
                          className="w-12 h-12 text-center text-xl border border-slate-300 rounded-md focus:outline-none focus:border-primary focus:ring-0"
                        />
                      ))}
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <button
                        type="button"
                        className={`text-primary text-sm font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed`}
                        onClick={async () => {
                          await handleSendOtpLogic();
                        }}
                        disabled={resendTimer > 0}
                      >
                        Resend OTP{resendTimer > 0 ? ` (${resendTimer}s)` : ""}
                      </button>
                    </div>
                    {error && (
                      <div className="text-red-500 mt-2 text-center">
                        {error}
                      </div>
                    )}
                    <div className="mt-5 flex gap-3 xl:mt-8 xl:text-left">
                      <Button
                        type="button"
                        variant="outline-secondary"
                        rounded
                        className="w-1/2 py-3.5"
                        onClick={() => setStep(0)}
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        rounded
                        className="w-1/2 bg-gradient-to-r from-primary/70 to-secondary/70 py-3.5 xl:mr-3"
                        loading={loading}
                        disabled={otp.some((d) => d === "")}
                      >
                        Verify OTP
                      </Button>
                    </div>
                  </form>
                )}
                {/* Step 3: New Password */}
                {step === 2 && (
                  <form onSubmit={handleChangePassword}>
                    <div className="w-full mt-3 relative">
                      <FormLabel>New Password*</FormLabel>

                      <FormInput
                        required
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        autoComplete="off"
                        onChange={(e) => setPassword(e.target.value)}
                        className="block px-4 py-3.5 rounded-[0.6rem] border-slate-300/80"
                        placeholder="Enter new password"
                      />
                      {showPassword ? (
                        <EyeOff
                          className="absolute bottom-2 right-2 cursor-pointer w-5 text-gray-500"
                          onClick={() => setShowPassword(false)}
                        />
                      ) : (
                        <Eye
                          className="absolute bottom-2 right-2 cursor-pointer w-5 text-gray-500"
                          onClick={() => setShowPassword(true)}
                        />
                      )}
                    </div>

                    <div className="text-xs text-gray-500 mt-2 mb-2">
                      <ul className="list-disc ml-5">
                        <li>At least 6 characters</li>
                        <li>At least 1 uppercase letter</li>
                        <li>At least 1 number</li>
                        <li>At least 1 special character</li>
                      </ul>
                    </div>
                    <div className="w-full mt-3 relative">
                      <FormLabel className="mt-4">Confirm Password*</FormLabel>
                      <FormInput
                        required
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="block px-4 py-3.5 rounded-[0.6rem] border-slate-300/80"
                        placeholder="Confirm new password"
                      />
                      {showConfirmPassword ? (
                        <EyeOff
                          className="absolute bottom-2 right-2 cursor-pointer w-5 text-gray-500"
                          onClick={() => setShowConfirmPassword(false)}
                        />
                      ) : (
                        <Eye
                          className="absolute bottom-2 right-2 cursor-pointer w-5 text-gray-500"
                          onClick={() => setShowConfirmPassword(true)}
                        />
                      )}
                    </div>

                    {error && (
                      <div className="text-red-500 mt-2 text-sm font-medium">
                        {error}
                      </div>
                    )}
                    <div className="mt-5 flex gap-3 xl:mt-8 xl:text-left">
                      <Button
                        type="submit"
                        variant="primary"
                        rounded
                        className="bg-gradient-to-r from-primary/70 to-secondary/70 py-3.5 xl:mr-3 w-full "
                        loading={loading}
                      >
                        Submit
                      </Button>
                    </div>
                  </form>
                )}
                {/* Step 4: Success */}
                {step === 3 && (
                  <div className="flex flex-col items-center justify-center">
                    <div className="text-green-600 text-2xl font-bold mb-4">
                      Password Reset Successful!
                    </div>
                    <div className="text-gray-600 mb-2">
                      You can now log in with your new password.
                    </div>
                    <div className="text-primary font-semibold mb-6">
                      Redirecting to login in {redirectTimer} seconds...
                    </div>
                    {/* No button, only timer and auto-redirect */}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-10 flex justify-center"></div>
          </div>
        </div>
      </div>
      {/* ...existing right-side and footer code... */}
      <div className="fixed container grid w-screen inset-0 h-screen grid-cols-12 lg:max-w-[1550px] 2xl:max-w-[1750px] pl-14 pr-12 xl:px-24">
        <div
          className={clsx([
            "relative h-screen col-span-12 lg:col-span-5 2xl:col-span-4 z-20",
            "after:bg-white after:hidden after:lg:block after:content-[''] after:absolute after:right-0 after:inset-y-0 after:bg-gradient-to-b after:from-white after:to-slate-100/80 after:w-[800%] after:rounded-[0_1.2rem_1.2rem_0/0_1.7rem_1.7rem_0]",
            "before:content-[''] before:hidden before:lg:block before:absolute before:right-0 before:inset-y-0 before:my-6 before:bg-gradient-to-b before:from-white/10 before:to-slate-50/10 before:bg-white/50 before:w-[800%] before:-mr-4 before:rounded-[0_1.2rem_1.2rem_0/0_1.7rem_1.7rem_0]",
          ])}
        ></div>
        <div
          className={clsx([
            "h-full col-span-7 2xl:col-span-8 lg:relative",
            "before:content-[''] before:absolute before:lg:-ml-10 before:left-0 before:inset-y-0 before:bg-gradient-to-b before:from-primary before:to-secondary before:w-screen before:lg:w-[800%]",
            "after:content-[''] after:absolute after:inset-y-0 after:left-0 after:w-screen after:lg:w-[800%] after:bg-texture-white after:bg-fixed after:bg-center after:lg:bg-[25rem_-25rem] after:bg-no-repeat",
          ])}
        >
          <div className="sticky top-0 z-10 flex-col justify-center hidden h-screen ml-16 lg:flex xl:ml-28 2xl:ml-36">
            <div className="leading-[1.4] text-[2.6rem] xl:text-5xl font-medium xl:leading-[1.2] text-white">
              Welcome to Millwoods Travels
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-row items-center justify-between p-4 z-10 absolute bottom-0 right-0">
        {crmVersion && (
          <div className="flex justify-start mr-10">
            <p className="text-gray-300 text-[12px]">{`v ${crmVersion}`}</p>
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = ({ authUser }: RootState) => ({ authUser });

const mapDispatchToProps = (dispatch: any) => ({});
export default connect(mapStateToProps, mapDispatchToProps)(Component);
